import { Inject, Injectable } from '@angular/core';
import { PaceInsightsService } from '@evorait/pace-logging';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { ConfigConstants } from '../../../config/config-constants';
import { OdataConfigInstanceToken, OdataConfigInterface } from '../../../interfaces/odata-config.interface';
import { LocalStorageCommonService } from '../../../services/local-storage/local-storage-common.service';
import { hydrateSlice, persistSlice, sliceHydrated } from '../../persistence';
import { configFailed, configLoaded, configLoadedFromStorage, initConfig } from '../actions/config.actions';
import { CONFIG_FEATURE_KEY } from '../config.state';
import * as ConfigSelectors from '../selectors/config.selector';
import { selectConfigLoaded } from '../selectors/config.selector';

/**
 * Owns the config load flow and the persistence mirror. `loadConfig$` fetches the config
 * from OData on every `initConfig` dispatch (initial boot and after sync), tracks the event
 * via insights, and caches the backend name to local storage; `persistConfig$` mirrors the
 * slice to the persistence layer after each backend load; `hydrateConfigOnInit$` reads the
 * cache back at startup so config is available offline while the network request is in flight.
 * Both hydrate steps are guarded on {@link selectConfigLoaded} so a slower cache read can
 * never overwrite fresher backend data.
 */
@Injectable()
export class ConfigEffects {
    public constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        @Inject(OdataConfigInstanceToken) private readonly odataConfigService: OdataConfigInterface,
        private readonly paceInsights: PaceInsightsService,
        private readonly localStorage: LocalStorageCommonService,
    ) {}

    /** Fetch config from the backend, track the event, and populate the slice. */
    public loadConfig$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initConfig),
            switchMap(() =>
                this.odataConfigService.getConfigState().pipe(
                    map((configValues) => configLoaded({ configValues })),
                    catchError(() => of(configFailed())),
                ),
            ),
        ),
    );

    public writeBackendNameToLocalStorage$ = createEffect(
        () =>
            this.store
                .select(ConfigSelectors.selectBackendName)
                .pipe(
                    tap((backendName) =>
                        this.localStorage.preInitializationStorageSet(
                            ConfigConstants.configKeyBackendName,
                            backendName,
                        ),
                    ),
                ),
        { dispatch: false },
    );

    /** Cache the slice whenever the backend load populates it. */
    public persistConfig$ = createEffect(() =>
        this.actions$.pipe(
            ofType(configLoaded),
            map(() => persistSlice({ featureKey: CONFIG_FEATURE_KEY })),
        ),
    );

    /** Rehydrate from the cache on init, but only while the backend has not yet populated the slice. */
    public hydrateConfigOnInit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initConfig),
            concatLatestFrom(() => this.store.select(selectConfigLoaded)),
            filter(([, loaded]) => !loaded),
            map(() => hydrateSlice({ featureKey: CONFIG_FEATURE_KEY })),
        ),
    );

    /** Bridge the generic hydrate result into the config slice, unless the backend already won. */
    public applyHydratedConfig$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sliceHydrated),
            filter(({ featureKey }) => featureKey === CONFIG_FEATURE_KEY),
            concatLatestFrom(() => this.store.select(selectConfigLoaded)),
            filter(([, loaded]) => !loaded),
            map(([{ data }]) =>
                configLoadedFromStorage({ configValues: (data as Record<string, string> | undefined) ?? {} }),
            ),
        ),
    );
}
